<template>
    <main v-if="user" class="flex justify-center">
        <HeaderComponent />
        <section class="w-full pb-[100px] lg:pl-[50px] lg:pt-[95px] pt-[95px] max-h-screen overflow-y-auto relative flex justify-start flex-col lg:p-[25px] p-[20px] h-auto min-h-screen bg-gray-50">
            <!-- Messages d'alerte -->
            <div class="fixed top-[80px] lg:left-[calc(50%+140px)] left-[50%] z-[100] translate-x-[-50%] w-[90%] max-w-[400px] flex flex-col">
                <div v-if="errorMessage" class="text-sm w-full rounded-lg text-white bg-red-600 my-[10px] font-medium text-center p-4 shadow-md">
                    {{ errorMessage }}
                </div>
                <div v-if="successMessage" class="text-sm w-full rounded-lg text-white bg-green-600 my-[10px] font-medium text-center p-4 shadow-md">
                    {{ successMessage }}
                </div>
            </div>

            <!-- Section Statistiques Financières -->
            <div v-if="user.isSuper" class="w-full grid grid-cols-1 lg:grid-cols-3 gap-4 my-4">
                <!-- Carte Montant Total -->
                <div class="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div class="flex justify-between items-center mb-4">
                        <div class="p-3 rounded-full bg-orange-100 text-orange-500">
                            <i class="fa-solid fa-circle-dollar-to-slot text-xl"></i>
                        </div>
                        <div :class="['w-10 h-10 rounded-full p-1 flex items-center justify-center', globalStats.totalSansReductionDuMois?.variation >= 0 ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600']">
                            <span class="text-xs font-bold">{{ globalStats.totalSansReductionDuMois?.variation.toFixed(2) }}%</span>
                        </div>
                    </div>
                    <h3 class="text-gray-500 text-sm font-medium mb-1">Montant total sans réduction</h3>
                    <p class="text-2xl font-bold text-gray-800 mb-2">FCFA {{ globalStats.totalSansReductionDuMois?.valeur }}</p>
                    <p class="text-xs text-gray-400 font-medium">Statistiques financières mensuelles</p>
                </div>

                <!-- Carte Recette Totale -->
                <div class="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div class="flex justify-between items-center mb-4">
                        <div class="p-3 rounded-full bg-blue-100 text-blue-500">
                            <i class="fa-solid fa-hand-holding-dollar text-xl"></i>
                        </div>
                        <div :class="['w-10 h-10 rounded-full p-1 flex items-center justify-center', globalStats.totalApresReductionDuMois?.variation >= 0 ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600']">
                            <span class="text-xs font-bold">{{ globalStats.totalApresReductionDuMois?.variation.toFixed(2) }}%</span>
                        </div>
                    </div>
                    <h3 class="text-gray-500 text-sm font-medium mb-1">Recette totale après réduction</h3>
                    <p class="text-2xl font-bold text-gray-800 mb-2">FCFA {{ globalStats.totalApresReductionDuMois?.valeur }}</p>
                    <p class="text-xs text-gray-400 font-medium">Statistiques financières mensuelles</p>
                </div>

                <!-- Carte Recette Complète -->
                <div class="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                    <div class="flex justify-between items-center mb-4">
                        <div class="p-3 rounded-full bg-purple-100 text-purple-500">
                            <i class="fa-solid fa-chart-line text-xl"></i>
                        </div>
                    </div>
                    <h3 class="text-gray-500 text-sm font-medium mb-1">Recette totale après réduction</h3>
                    <p class="text-2xl font-bold text-gray-800 mb-2">FCFA {{ globalStats.totalApresReductionTotal }}</p>
                    <p class="text-xs text-gray-400 font-medium">Statistiques financières complètes</p>
                </div>
            </div>

            <!-- Section Stocks -->
            <div v-if="user.isSuper" class="w-full my-6">
                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                    <!-- Stock Général -->
                    <div class="bg-orange-500 text-white rounded-xl p-4 shadow-md hover:shadow-lg transition-shadow cursor-pointer">
                        <div class="flex justify-center mb-3">
                            <i class="fa-solid fa-layer-group text-2xl"></i>
                        </div>
                        <h3 class="text-sm font-bold text-center mb-2">Stock Général</h3>
                        <p class="text-xl font-semibold text-center mb-1">{{ getTotalFilteredProduits().totalGeneralQuantite }} Articles</p>
                        <p class="text-xs text-orange-100 text-center">Montant: {{ getTotalFilteredProduits().totalGeneralPrixReduit }} FCFA</p>
                    </div>

                    <!-- Autres Stocks -->
                    <template v-for="(stock, index) in [
                        { name: 'Stock Calavi', icon: 'truck' },
                        { name: 'Stock Parakou', icon: 'truck' },
                        { name: 'Magasin Calavi', icon: 'store' },
                        { name: 'Magasin Parakou', icon: 'store' }
                    ]" :key="index">
                        <div class="bg-gray-700 text-white rounded-xl p-4 shadow-md hover:shadow-lg transition-shadow cursor-pointer">
                            <div class="flex justify-center mb-3">
                                <i :class="`fa-solid fa-${stock.icon} text-2xl`"></i>
                            </div>
                            <h3 class="text-sm font-bold text-center mb-2">{{ stock.name }}</h3>
                            <p class="text-xl font-semibold text-center mb-1">{{ getStockTotal(stock.name).totalQuantite }} Articles</p>
                            <p class="text-xs text-gray-300 text-center">Montant: {{ getStockTotal(stock.name).totalPrixReduit }} FCFA</p>
                        </div>
                    </template>
                </div>
            </div>

            <!-- Section Statistiques et Graphiques -->
            <div v-if="user.isSuper" class="w-full my-6">
                <!-- Statistiques de Commandes -->
                <div class="grid lg:grid-cols-4 md:grid-cols-2 gap-4">
                    <div v-for="(stat, index) in [
                        { 
                            title: 'Commandes', 
                            value: dailyStats.total?.valeur, 
                            icon: 'cart-shopping',
                            change: dailyStats.total?.variation,
                            color: 'orange'
                        },
                        { 
                            title: 'Boutique', 
                            value: dailyStats.enBoutique?.valeur, 
                            icon: 'shop',
                            change: dailyStats.enBoutique?.variation,
                            color: 'blue'
                        },
                        { 
                            title: 'En ligne', 
                            value: dailyStats.enLigne?.valeur, 
                            icon: 'globe',
                            change: dailyStats.enLigne?.variation,
                            color: 'purple'
                        },
                        { 
                            title: 'Réductions', 
                            value: dailyStats.reduction?.valeur, 
                            icon: 'tags',
                            change: dailyStats.reduction?.variation,
                            color: 'green'
                        }
                    ]" :key="index" class="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
                        <div class="flex justify-between items-center mb-3">
                            <h3 class="text-xs font-bold text-gray-500">{{ stat.title }}</h3>
                            <div :class="`p-2 rounded-full bg-${stat.color}-100 text-${stat.color}-500`">
                                <i :class="`fa-solid fa-${stat.icon}`"></i>
                            </div>
                        </div>
                        <p class="text-2xl font-bold text-gray-800 mb-2">{{ stat.value }}</p>
                        <div class="flex items-center justify-between">
                            <div :class="`flex items-center text-xs font-medium ${stat.change >= 0 ? 'text-green-600' : 'text-red-600'}`">
                                <i :class="`fa-solid fa-arrow-${stat.change >= 0 ? 'up' : 'down'} mr-1`"></i>
                                <span>{{ stat.change }}%</span>
                            </div>
                            <span class="text-xs text-gray-400 font-medium">{{ dayjs().format('DD/MM/YYYY') }}</span>
                        </div>
                    </div>
                </div>

                <!-- Graphique 1 - Ventes -->
                <div class="bg-white rounded-xl hidden p-4 shadow-sm hover:shadow-md transition-shadow">
                    <h3 class="text-sm font-bold text-gray-700 mb-4">Évolution des ventes</h3>
                    <div class="chart-container">
                        <canvas id="myChart" ref="myChart"></canvas>
                    </div>
                </div>
            </div>

            <!-- Graphique 2 - Revenus -->
            <div v-if="user.isSuper" class="w-full my-6 md:flex hidden">
                <div class="bg-white rounded-xl w-full md:h-auto relative p-4 shadow-sm hover:shadow-md transition-shadow">
                    <div class="flex justify-between items-center flex-wrap mb-4">
                        <h3 class="text-sm font-bold text-gray-700">
                            <span v-if="selectedChartType === 'revenue'">Évolution des revenus</span>
                            <span v-else>Évolution du nombre de ventes</span>
                        </h3>
                        <button 
                            @click="showAdvancedFilters = !showAdvancedFilters"
                            class="flex items-center gap-2 bg-orange-100 text-orange-600 py-1 px-3 rounded-lg hover:bg-orange-200 transition-colors text-sm font-medium"
                        >
                            <i class="fa-solid fa-filter"></i>
                            Filtres avancés
                            <i :class="showAdvancedFilters ? 'fa-chevron-up' : 'fa-chevron-down'" class="fa-solid text-xs"></i>
                        </button>
                    </div>

                    <!-- Filtres de base -->
                    <div class="flex gap-2 flex-wrap mb-4">
                        <select
                            v-model="selectedChartType"
                            @change="onBaseFilterChange()"
                            class="bg-gray-100 border border-gray-300 text-gray-700 py-1 px-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        >
                            <option value="revenue">Revenus</option>
                            <option value="sales">Nombre de ventes</option>
                        </select>
                        <select
                            v-model="selectedYear"
                            @change="onBaseFilterChange()"
                            class="bg-gray-100 border border-gray-300 text-gray-700 py-1 px-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        >
                            <option v-for="year in availableYears" :key="year" :value="year">
                                {{ year }}
                            </option>
                        </select>
                        <select
                            v-model="selectedBoutique"
                            @change="onBaseFilterChange()"
                            class="bg-gray-100 border border-gray-300 text-gray-700 py-1 px-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                        >
                            <option value="all">Toutes les boutiques</option>
                            <option
                                v-for="boutique in availableBoutiques"
                                :key="boutique"
                                :value="boutique"
                            >
                                {{ boutique }}
                            </option>
                        </select>
                    </div>

                    <!-- Filtres avancés -->
                    <div v-if="showAdvancedFilters" class="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200">
                        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <!-- Filtre par catégorie (multi-sélection) -->
                            <div class="flex flex-col relative">
                                <label class="text-xs font-medium text-gray-500 mb-1">Catégorie</label>
                                <button
                                    type="button"
                                    @click="showCategoryDropdown = !showCategoryDropdown"
                                    class="bg-white border border-gray-300 text-gray-700 py-2 px-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm text-left flex justify-between items-center gap-2"
                                >
                                    <span class="truncate">{{ filters.categories.length ? filters.categories.join(', ') : 'Toutes les catégories' }}</span>
                                    <i :class="showCategoryDropdown ? 'fa-chevron-up' : 'fa-chevron-down'" class="fa-solid text-xs text-gray-400"></i>
                                </button>
                                <div v-if="showCategoryDropdown" class="absolute top-full left-0 mt-1 w-full min-w-[180px] bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-52 overflow-y-auto">
                                    <label v-for="cat in filterOptions.categories" :key="cat" class="flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer">
                                        <input type="checkbox" :value="cat" v-model="filters.categories" class="rounded text-orange-500 focus:ring-orange-500">
                                        {{ cat }}
                                    </label>
                                    <button type="button" @click="showCategoryDropdown = false" class="w-full text-center text-xs text-orange-600 font-medium py-2 border-t border-gray-100 hover:bg-gray-50">Fermer</button>
                                </div>
                            </div>

                            <!-- Filtre par vendeur -->
                            <div class="flex flex-col">
                                <label class="text-xs font-medium text-gray-500 mb-1">Vendeur</label>
                                <select 
                                    v-model="filters.userId"
                                    class="bg-white border border-gray-300 text-gray-700 py-2 px-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                                >
                                    <option value="">Tous les vendeurs</option>
                                    <option v-for="seller in filterOptions.sellers" :key="seller._id" :value="seller._id">
                                        {{ seller.name }} ({{ seller.boutique }})
                                    </option>
                                </select>
                            </div>

                            <!-- Type de filtre date -->
                            <div class="flex flex-col">
                                <label class="text-xs font-medium text-gray-500 mb-1">Type de date</label>
                                <select 
                                    v-model="filters.dateType"
                                    class="bg-white border border-gray-300 text-gray-700 py-2 px-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                                >
                                    <option value="none">Pas de filtre date</option>
                                    <option value="exact">Date exacte</option>
                                    <option value="period">Période</option>
                                </select>
                            </div>

                            <!-- Date exacte -->
                            <div v-if="filters.dateType === 'exact'" class="flex flex-col">
                                <label class="text-xs font-medium text-gray-500 mb-1">Date exacte</label>
                                <input 
                                    type="date" 
                                    v-model="filters.exactDate"
                                    class="bg-white border border-gray-300 text-gray-700 py-2 px-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                                />
                            </div>

                            <!-- Période - Date de début -->
                            <div v-if="filters.dateType === 'period'" class="flex flex-col">
                                <label class="text-xs font-medium text-gray-500 mb-1">Date de début</label>
                                <input 
                                    type="date" 
                                    v-model="filters.startDate"
                                    class="bg-white border border-gray-300 text-gray-700 py-2 px-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                                />
                            </div>

                            <!-- Période - Date de fin -->
                            <div v-if="filters.dateType === 'period'" class="flex flex-col">
                                <label class="text-xs font-medium text-gray-500 mb-1">Date de fin</label>
                                <input 
                                    type="date" 
                                    v-model="filters.endDate"
                                    class="bg-white border border-gray-300 text-gray-700 py-2 px-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                                />
                            </div>
                        </div>

                        <!-- Boutons d'action -->
                        <div class="flex gap-3 mt-4 pt-4 border-t border-gray-200">
                            <button 
                                @click="applyFilters()"
                                class="flex items-center gap-2 bg-orange-500 text-white py-2 px-4 rounded-lg hover:bg-orange-600 transition-colors text-sm font-medium"
                            >
                                <i class="fa-solid fa-check"></i>
                                Appliquer les filtres
                            </button>
                            <button 
                                @click="resetFilters()"
                                class="flex items-center gap-2 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
                            >
                                <i class="fa-solid fa-rotate-left"></i>
                                Réinitialiser
                            </button>
                        </div>

                        <!-- Indicateur des filtres actifs -->
                        <div v-if="hasActiveFilters" class="flex flex-wrap gap-2 mt-4">
                            <span class="text-xs font-medium text-gray-500">Filtres actifs:</span>
                            <span v-if="filters.categories.length" class="inline-flex items-center gap-1 bg-orange-100 text-orange-700 text-xs px-2 py-1 rounded-full">
                                Catégorie: {{ filters.categories.join(', ') }}
                                <button @click="filters.categories = []; applyFilters()" class="hover:text-orange-900">&times;</button>
                            </span>
                            <span v-if="filters.userId" class="inline-flex items-center gap-1 bg-purple-100 text-purple-700 text-xs px-2 py-1 rounded-full">
                                Vendeur: {{ getSellerName(filters.userId) }}
                                <button @click="filters.userId = ''; applyFilters()" class="hover:text-purple-900">&times;</button>
                            </span>
                            <span v-if="filters.dateType === 'exact' && filters.exactDate" class="inline-flex items-center gap-1 bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full">
                                Date: {{ filters.exactDate }}
                                <button @click="filters.exactDate = ''; filters.dateType = 'none'; applyFilters()" class="hover:text-green-900">&times;</button>
                            </span>
                            <span v-if="filters.dateType === 'period' && (filters.startDate || filters.endDate)" class="inline-flex items-center gap-1 bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full">
                                Période: {{ filters.startDate || '...' }} au {{ filters.endDate || '...' }}
                                <button @click="filters.startDate = ''; filters.endDate = ''; filters.dateType = 'none'; applyFilters()" class="hover:text-green-900">&times;</button>
                            </span>
                        </div>
                    </div>

                    <!-- Loading indicator -->
                    <div v-if="isLoadingChart" class="flex items-center justify-center h-[300px]">
                        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
                    </div>

                    <div v-else class="chart-container1" style="height: 380px;">
                        <canvas id="myChart1" ref="myChart1"></canvas>
                    </div>
                </div>
            </div>

            <!-- Historique des Ventes -->
            <div class="w-full my-6">
                <div class="bg-white rounded-xl relative w-full overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                    <h3 class="text-lg font-bold text-gray-700 p-4 border-b">Historique des ventes</h3>
                    
                    <div class="w-full relative">
                        <table class="w-full relative">
                            <thead>
                                <tr class="bg-gray-100 text-gray-600 text-sm font-semibold uppercase">
                                    <th class="px-4 py-3 text-left hidden md:table-cell">ID</th>
                                    <th class="md:px-4 px-[10px] py-3 text-left">Client</th>
                                    <th class="px-4 py-3 text-left hidden md:table-cell">Téléphone</th>
                                    <th class="px-4 py-3 text-left hidden md:table-cell">Vendeur</th>
                                    <th class="md:px-4 px-[10px] py-3 text-left">Produits</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(vente, index) in salesToday" :key="index" class="border-b border-gray-200 hover:bg-gray-50">
                                    <td class="px-4 py-3 text-sm text-gray-600 hidden md:table-cell">#{{ index + 1 }}</td>
                                    <td class="md:px-4 px-[10px] py-3 text-sm text-gray-700">
                                        {{ vente.client ? vente.client.nom : 'N/A' }} {{ vente.client ? vente.client.prenoms : '' }}
                                    </td>
                                    <td class="px-4 py-3 text-sm text-gray-600 hidden md:table-cell">
                                        {{ vente.client ? vente.client.numero : 'N/A' }}
                                    </td>
                                    <td class="px-4 py-3 text-sm text-gray-600 hidden md:table-cell">
                                        {{ vente.vendeur ? vente.vendeur.nom : 'N/A' }} {{ vente.vendeur ? vente.vendeur.prenoms : '' }}
                                    </td>
                                    <td class="md:px-4 px-[10px] py-3 text-sm text-gray-700">
                                        {{ vente.produits.map(p => `${p.lotName} (${p.quantity})`).join(', ') }}
                                    </td>
                                </tr>
                                <tr v-if="salesToday.length === 0">
                                    <td colspan="5" class="px-4 py-12 text-center text-gray-500 font-medium">
                                        Aucune vente enregistrée aujourd'hui !
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div class="bg-gray-100 px-4 py-3 text-sm text-gray-700 font-semibold">
                        <p>Total : <span class="text-orange-500 font-bold text-lg ml-2">{{ totalSalesToday }} FCFA</span></p>
                    </div>
                </div>
            </div>
        </section>
    </main>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import axios from 'axios';
import dayjs from 'dayjs';
import HeaderComponent from '@/components/HeaderComponent.vue';
import { useAuthStore } from '@/store/auth';
import { Line } from 'vue-chartjs';
import { Chart, registerables } from 'chart.js';
import { Chart as ChartJS, Title, Tooltip, Legend, LineElement, PointElement, CategoryScale, LinearScale, Filler } from 'chart.js';

ChartJS.register(Title, Tooltip, Legend, LineElement, PointElement, CategoryScale, LinearScale, Filler);

Chart.register(...registerables);

const authStore = useAuthStore();
const user = computed(() => authStore.user);

const totalSalesToday = ref(0)
const produits = ref([])
const errorMessage = ref('')
const totalSalesCount = ref(0)
const successMessage = ref('')    
const filteredSales = ref([]);
const selectedBoutique = ref('all');
const dataGraphCount = ref([]);
const salesChart = ref(null);
const selectedChartType = ref('revenue');

// Filtres avancés
const showAdvancedFilters = ref(false);
const isLoadingChart = ref(false);
const filterOptions = ref({
    categories: [],
    brands: [],
    productNames: [],
    sellers: []
});

const filters = ref({
    categories: [],
    userId: '',
    dateType: 'none', // 'none', 'exact', 'period'
    exactDate: '',
    startDate: '',
    endDate: ''
});
const showCategoryDropdown = ref(false);

// Computed pour savoir si des filtres sont actifs
const hasActiveFilters = computed(() => {
    return filters.value.categories.length > 0 ||
           filters.value.userId ||
           (filters.value.dateType === 'exact' && filters.value.exactDate) ||
           (filters.value.dateType === 'period' && (filters.value.startDate || filters.value.endDate));
});

// Récupérer le nom du vendeur
const getSellerName = (userId) => {
    const seller = filterOptions.value.sellers.find(s => s._id === userId);
    return seller ? seller.name : 'Inconnu';
};

// Récupérer les options de filtrage
const fetchFilterOptions = async () => {
    try {
        const response = await axios.get('/api/sales/stats/filter-options');
        filterOptions.value = response.data;
    } catch (error) {
        console.error('Erreur lors du chargement des options de filtre:', error);
    }
};

// Appliquer les filtres
const applyFilters = async () => {
    isLoadingChart.value = true;
    
    try {
        // Si aucun filtre avancé n'est actif, utiliser les endpoints par défaut
        if (!hasActiveFilters.value) {
            const graphPromise = axios.get('/api/sales/stats/graph');
            const graphCountPromise = axios.get('/api/sales/stats/graph-count');
            
            const [graphResponse, graphCountResponse] = await Promise.all([graphPromise, graphCountPromise]);
            
            dataGraph.value = graphResponse.data;
            dataGraphCount.value = graphCountResponse.data;
            captureBaseYearsAndBoutiques();

            // Ne retomber sur l'année la plus récente que si l'année sélectionnée n'existe
            // plus dans les données (ex: juste après un reset) - sinon on écrase le choix de l'utilisateur.
            if (dataGraph.value.length > 0) {
                const years = [...new Set(dataGraph.value.map(item => item.year))];
                if (!years.includes(selectedYear.value) && years.length > 0) {
                    selectedYear.value = Math.max(...years);
                }
            }
        } else {
            // Construire les paramètres de la requête
            const params = {
                type: selectedChartType.value
            };

            if (filters.value.categories.length) {
                params.category = filters.value.categories.join(',');
            }
            if (filters.value.userId) {
                params.userId = filters.value.userId;
            }
            if (selectedBoutique.value !== 'all') {
                params.boutique = selectedBoutique.value;
            }
            if (filters.value.dateType === 'exact' && filters.value.exactDate) {
                params.exactDate = filters.value.exactDate;
            }
            if (filters.value.dateType === 'period') {
                if (filters.value.startDate) {
                    params.startDate = filters.value.startDate;
                }
                if (filters.value.endDate) {
                    params.endDate = filters.value.endDate;
                }
            }

            const response = await axios.get('/api/sales/stats/graph-filtered', { params });
            dataGraph.value = response.data;
            dataGraphCount.value = response.data;
            // Ne pas toucher à selectedYear ici : ce fetch ne dépend pas de l'année choisie
            // (le filtre n'envoie pas l'année au serveur), donc l'absence de données pour l'année
            // sélectionnée est un résultat valide (graphique vide), pas une raison de la changer.
        }

        // Désactiver le loading AVANT de créer le graphique pour que le canvas soit rendu
        isLoadingChart.value = false;
        
        // Attendre que le DOM soit mis à jour puis créer le graphique
        await nextTick();
        createChart();
        
    } catch (error) {
        errorMessage.value = `Erreur lors de l'application des filtres : ${error.response ? error.response.data.message : 'Vérifiez votre connexion internet'}`;
        hideErrorMessageAfterDelay();
        isLoadingChart.value = false;
    }
};

// Changement d'un filtre de base (type/année/boutique) : le jeu de données complet est déjà
// chargé, on se contente de retrancher le graphique côté client - sauf si des filtres avancés
// (catégorie, vendeur, date) sont actifs, auquel cas il faut redemander les données filtrées au serveur.
const onBaseFilterChange = () => {
    if (hasActiveFilters.value) {
        applyFilters();
    } else {
        createChart();
    }
};

// Réinitialiser les filtres
const resetFilters = () => {
    filters.value = {
        categories: [],
        userId: '',
        dateType: 'none',
        exactDate: '',
        startDate: '',
        endDate: ''
    };
    selectedBoutique.value = 'all';
    applyFilters();
};

const globalStats = ref({});
const fetchProduitsStats = async () => {
  try {
    const response = await axios.get('/api/sales/stats');
    globalStats.value = response.data;
  } catch (error) {
    errorMessage.value = `Erreur lors du chargement des statistiques : ${error.response ? error.response.data.message : 'Vérifiez votre connexion internet'}`;
    hideErrorMessageAfterDelay();
  }
};

const dailyStats = ref({});
const fetchProduitsDaylyStats = async () => {
  try {
    const response = await axios.get('/api/sales/stats/daily');
    dailyStats.value = response.data;
  } catch (error) {
    errorMessage.value = `Erreur lors du chargement des statistiques : ${error.response ? error.response.data.message : 'Vérifiez votre connexion internet'}`;
    hideErrorMessageAfterDelay();
  }
};

const stockStats = ref({})
const fetchProduitsStock = async () => {
  try {
    const response = await axios.get('/api/lots/stats/stock');
    stockStats.value = response.data;
  } catch (error) {
    errorMessage.value = `Erreur lors du chargement du stock : ${error.response ? error.response.data.message : 'Vérifiez votre connexion internet'}`;
    hideErrorMessageAfterDelay();
  }
};

const dataGraph = ref([]);
const selectedYear = ref(new Date().getFullYear());
const revenueChart = ref(null);

// Années/boutiques disponibles pour les sélecteurs : capturées une fois depuis les données NON
// filtrées, pour ne pas rétrécir quand un filtre avancé (catégorie, vendeur...) ne matche rien sur
// une année/boutique donnée (ex: aucune vente "Accessoires" en 2024 ne doit pas faire disparaître
// 2024 du sélecteur).
const baseYears = ref([]);
const baseBoutiques = ref([]);
const captureBaseYearsAndBoutiques = () => {
    const yearsFromRevenue = dataGraph.value.map(item => item.year);
    const yearsFromSales = dataGraphCount.value.map(item => item.year);
    baseYears.value = [...new Set([...yearsFromRevenue, ...yearsFromSales])].sort((a, b) => b - a);

    const boutiquesFromRevenue = dataGraph.value.map(item => item.boutique);
    const boutiquesFromSales = dataGraphCount.value.map(item => item.boutique);
    baseBoutiques.value = [...new Set([...boutiquesFromRevenue, ...boutiquesFromSales])].sort();
};

const availableYears = computed(() => baseYears.value);
const availableBoutiques = computed(() => baseBoutiques.value);

const getMonthName = (monthIndex) => {
    const date = new Date(2000, monthIndex - 1, 1);
    return date.toLocaleString('default', { month: 'short' });
};

const fetchProduitsGraph = async () => {
    try {
        const response = await axios.get('/api/sales/stats/graph');
        dataGraph.value = response.data;
        
        // Sélectionner l'année la plus récente par défaut
        if (dataGraph.value.length > 0) {
            const years = [...new Set(dataGraph.value.map(item => item.year))];
            selectedYear.value = Math.max(...years);
        }
        
        await nextTick();
        createChart();
    } catch (error) {
        errorMessage.value = `Erreur lors du chargement du graphique : ${error.response ? error.response.data.message : 'Vérifiez votre connexion internet'}`;
        hideErrorMessageAfterDelay();
    }
};

const fetchProduitsGraphCount = async () => {
    try {
        const response = await axios.get('/api/sales/stats/graph-count');
        dataGraphCount.value = response.data;
        captureBaseYearsAndBoutiques();
    } catch (error) {
        errorMessage.value = `Erreur lors du chargement du graphique des ventes : ${error.response ? error.response.data.message : 'Vérifiez votre connexion internet'}`;
        hideErrorMessageAfterDelay();
    }
};

// Modifiez la fonction createRevenueChart pour gérer les deux types de graphiques
const createChart = () => {
    const ctx = document.getElementById('myChart1')?.getContext('2d');
    if (!ctx) {
        console.warn('Canvas myChart1 non trouvé, tentative reportée');
        return;
    }

    // Détruire tous les graphiques existants
    if (revenueChart.value) {
        revenueChart.value.destroy();
        revenueChart.value = null;
    }
    if (salesChart.value) {
        salesChart.value.destroy();
        salesChart.value = null;
    }

    // Choisir les données en fonction du type de graphique
    const dataSource = selectedChartType.value === 'revenue' ? dataGraph.value : dataGraphCount.value;
    const dataKey = selectedChartType.value === 'revenue' ? 'totalRevenue' : 'salesCount';
    const labelSuffix = selectedChartType.value === 'revenue' ? 'FCFA' : 'ventes';
    
    // Filtrer les données pour l'année sélectionnée
    const yearData = dataSource.filter(item => item.year === selectedYear.value);
    
    // Couleurs pour chaque boutique
    const boutiqueColors = {
        'Calavi': '#4f46e5', // violet
        'Parakou': '#10b981', // vert
        'En ligne': '#f59e0b' // orange
    };

    // Préparer les datasets
    let datasets = [];

    if (selectedBoutique.value === 'all') {
        datasets = yearData.map(boutiqueData => {
            const monthlyData = Array(12).fill(0);
            const monthlyItems = boutiqueData.monthlyRevenue || boutiqueData.monthlySales || [];
            
            monthlyItems.forEach(monthData => {
                monthlyData[monthData.month - 1] = monthData[dataKey];
            });

            return {
                label: `${boutiqueData.boutique} ${selectedYear.value}`,
                data: monthlyData,
                borderColor: boutiqueColors[boutiqueData.boutique] || '#4f46e5',
                backgroundColor: `${boutiqueColors[boutiqueData.boutique]}20`,
                borderWidth: 2,
                tension: 0.4,
                fill: true,
                pointBackgroundColor: boutiqueColors[boutiqueData.boutique],
                pointBorderColor: '#fff',
                pointRadius: 4,
                pointHoverRadius: 6
            };
        });
    } else {
        const boutiqueData = yearData.find(item => item.boutique === selectedBoutique.value);
        
        if (boutiqueData) {
            const monthlyData = Array(12).fill(0);
            const monthlyItems = boutiqueData.monthlyRevenue || boutiqueData.monthlySales || [];
            
            monthlyItems.forEach(monthData => {
                monthlyData[monthData.month - 1] = monthData[dataKey];
            });

            datasets = [{
                label: `${selectedBoutique.value} ${selectedYear.value}`,
                data: monthlyData,
                borderColor: boutiqueColors[selectedBoutique.value] || '#4f46e5',
                backgroundColor: `${boutiqueColors[selectedBoutique.value]}20`,
                borderWidth: 2,
                tension: 0.4,
                fill: true,
                pointBackgroundColor: boutiqueColors[selectedBoutique.value],
                pointBorderColor: '#fff',
                pointRadius: 4,
                pointHoverRadius: 6
            }];
        }
    }

    // Créer les labels des mois
    const monthLabels = Array.from({ length: 12 }, (_, i) => getMonthName(i + 1));

    // Créer le graphique
    const newChart = new Chart(ctx, {
        type: 'line',
        data: {
            labels: monthLabels,
            datasets: datasets
        },
        options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    position: 'top',
                    labels: {
                        usePointStyle: true,
                        padding: 20
                    }
                },
                tooltip: {
                    callbacks: {
                        label: function(context) {
                            return `${context.dataset.label}: ${context.parsed.y.toLocaleString()} ${labelSuffix}`;
                        },
                        title: function(context) {
                            return `${getMonthName(context[0].dataIndex + 1)} ${selectedYear.value}`;
                        }
                    }
                }
            },
            scales: {
                x: {
                    grid: {
                        display: false
                    },
                    title: {
                        display: true,
                        text: 'Mois',
                        color: '#6b7280'
                    }
                },
                y: {
                    beginAtZero: true,
                    ticks: {
                        callback: function(value) {
                            return value.toLocaleString() + (selectedChartType.value === 'revenue' ? ' FCFA' : '');
                        }
                    },
                    title: {
                        display: true,
                        text: selectedChartType.value === 'revenue' ? 'Montant' : 'Nombre de ventes',
                        color: '#6b7280'
                    }
                }
            }
        }
    });

    // Stocker la référence du graphique
    if (selectedChartType.value === 'revenue') {
        revenueChart.value = newChart;
    } else {
        salesChart.value = newChart;
    }
};

// Modifiez updateChart
const updateChart = () => {
    createChart();
};


const getTotalFilteredProduits = () => {
  const global = stockStats.value.global || { nombre: 0, montant: 0 }
  return {
    totalGeneralQuantite: global.nombre,
    totalGeneralPrixReduit: global.montant.toLocaleString()
  }
}

// Pour chaque stock (ex: 'Stock Calavi')
const getStockTotal = (stockName) => {
  const key = stockName.toLowerCase() // adapter à ta base de données
  const data = stockStats.value[key] || { nombre: 0, montant: 0 }
  return {
    totalQuantite: data.nombre,
    totalPrixReduit: data.montant.toLocaleString()
  }
}


const totalOrders = ref(0);
const boutiqueOrders = ref(0);
const onlineOrders = ref(0);
const totalAmount = ref(0);
const totalRevenue = ref(0);
const totalRevenueAll = ref(0)
const percentageReduction = ref(0);
const percentageChangeTotalOrders = ref(0);
const percentageChangeBoutiqueOrders = ref(0);
const percentageChangeOnlineOrders = ref(0);
const percentageChangeTotalAmount = ref(0);
const percentageChangeTotalRevenue = ref(0);
const percentageChangeRevenue = ref(0);
const salesToday = ref([])

const fetchSalesData = async () => {
    try {
        const params = {
            page: 1,
            limit: 20,
            isSuper: user.value.isSuper,
            userId: user.value._id,
            today: 'true'
        };

        const { data } = await axios.get('/api/sales', { params });
        
        salesToday.value = data.sales;
        totalSalesToday.value = data.stats.totalAllAmount;
        
    } catch (error) {
        errorMessage.value = "Erreur lors de la récupération des ventes";
        hideErrorMessageAfterDelay();
    }
}


onMounted(async () => {
    await fetchSalesData();
    
    if (user.value.isSuper) {
        await fetchProduitsStats();
        await fetchProduitsDaylyStats();
        await fetchProduitsStock();
        await fetchProduitsGraph();
        await fetchProduitsGraphCount();
        await fetchFilterOptions();
    }
});

const hideSuccessMessageAfterDelay = () => {
    setTimeout(() => {
      successMessage.value = ''; 
    }, 5000);
};
  
const hideErrorMessageAfterDelay = () => {
    setTimeout(() => {
      errorMessage.value = ''; 
    }, 10000);
};

</script>
<style scoped>
.chart-container, .chart-container1 {
  position: relative;
  width: 100%;
  height: 380px;
}


/* Transition pour les cartes */
.transition-shadow {
  transition: box-shadow 0.3s ease;
}

/* Style pour les tableaux */
</style>